/**
 * What a guest can tell us went wrong, and why it is these six.
 *
 * The result screen has had exactly one feedback channel since it shipped: a
 * five-star tap. Across ~30,600 rated sessions it reads 88% five stars and 3%
 * one-or-two — which is a healthy number and a useless one, because a 1★ tells
 * you a guest was unhappy and nothing whatsoever about what to fix. The ~910
 * unhappy sessions are the most valuable signal the booth produces and they
 * currently arrive as a single integer.
 *
 * The aspects below are not a generic satisfaction survey. Each one is a thing
 * this platform can actually change, and each maps to a place a fix lands:
 *
 *   photo    → camera settings, filter/adjustment tuning, lighting
 *   print    → printer routing, paper/ribbon, frame composition
 *   timing   → capture countdown and retake settings
 *   choices  → the project's frame and filter catalogue
 *   payment  → gateway, QR quoting, voucher handling
 *   digital  → gallery upload, QR, email delivery
 *
 * Deliberately NOT here: anything the software cannot move. "The venue was
 * cramped", "the queue was long" and "the staff were rude" are real complaints
 * and belong to the operator, not to a booth release — putting them on this
 * screen buys sympathy and no fix, and every extra card costs attention on a
 * kiosk a guest is standing at for a few seconds.
 *
 * Order matters: the two that actually break (photo, print) come first, so a
 * guest who reads one row and taps has already told us the most likely thing.
 *
 * `key` is persisted and aggregated server-side — treat these strings as a
 * stable wire format. Add new aspects at the end; never repurpose a key.
 */

import type { IconType } from 'react-icons'
import { FaCamera, FaPrint, FaRegClock, FaPalette, FaQrcode, FaMobileAlt } from 'react-icons/fa'

export interface FeedbackAspect {
  /** Stable, persisted identifier. Never change one of these. */
  key: string
  /** i18n suffix under `result.feedback.aspects.*`. */
  i18nKey: string
  icon: IconType
}

export const FEEDBACK_ASPECTS: readonly FeedbackAspect[] = [
  { key: 'photo', i18nKey: 'photo', icon: FaCamera },
  { key: 'print', i18nKey: 'print', icon: FaPrint },
  { key: 'timing', i18nKey: 'timing', icon: FaRegClock },
  { key: 'choices', i18nKey: 'choices', icon: FaPalette },
  { key: 'payment', i18nKey: 'payment', icon: FaQrcode },
  { key: 'digital', i18nKey: 'digital', icon: FaMobileAlt }
] as const

/**
 * At or below this, the booth opens the detail modal by itself.
 *
 * Two stars is the line because it is where the rating stops being a rating and
 * starts being a complaint. Above it the modal is offered, not imposed: a happy
 * guest interrupted by a survey is a worse experience than the one they just
 * rated highly, and 97% of sessions are above the line.
 */
export const FEEDBACK_AUTO_PROMPT_MAX_RATING = 2

/** One aspect's answer. Aspects the guest didn't touch are simply absent. */
export interface AspectScore {
  key: string
  score: number
}

export function isLowRating(rating: number | null | undefined): boolean {
  return typeof rating === 'number' && rating >= 1 && rating <= FEEDBACK_AUTO_PROMPT_MAX_RATING
}

/**
 * Normalise the answer map for persistence: fixed aspect order, scores clamped
 * to the 1–5 the Session schema enumerates, unanswered aspects dropped.
 *
 * Dropping rather than zero-filling is the point — "did not answer" and "rated
 * this 1" are different facts, and a survey that forces six answers to collect
 * one gets abandoned instead of submitted.
 */
export function toAspectScores(answers: Record<string, number | null>): AspectScore[] {
  return FEEDBACK_ASPECTS.map((aspect) => ({ key: aspect.key, score: answers[aspect.key] })).filter(
    (entry): entry is AspectScore =>
      typeof entry.score === 'number' && entry.score >= 1 && entry.score <= 5
  )
}
